import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Printer, Upload, ArrowLeft, Truck, FileText, Package, MapPin, CheckCircle2, Trash2, MessageCircle, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import TransportCopyUploadDialog from "../components/TransportCopyUploadDialog";
import { OrderCustomerPanel } from "../components/OrderCustomerPanel";
import { useAuth } from "../contexts/AuthContext";

const STATUS_STEPS = [
    { key: "placed", label: "Placed", icon: <CheckCircle2 className="w-4 h-4" /> },
    { key: "confirmed", label: "Confirmed", icon: <CheckCircle2 className="w-4 h-4" /> },
    { key: "packing", label: "Packing", icon: <Package className="w-4 h-4" /> },
    { key: "packed", label: "Packed", icon: <Package className="w-4 h-4" /> },
    { key: "shipped", label: "Shipped", icon: <Truck className="w-4 h-4" /> },
    { key: "delivered", label: "Delivered", icon: <MapPin className="w-4 h-4" /> },
];

/** Order detail — used by:
 *   - Staff (admin/editor/MR/AB) at /employee/order-status/:id
 *   - Distributor at /distributor/orders/:id
 *   - Customer at /my-orders/:id
 * Print button + Transport upload only show to staff. Distributor + customer
 * see the transport copy download link if one has been uploaded.
 */
export default function OrderDetailPage() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [busy, setBusy] = useState(false);
    const [retry, setRetry] = useState(null);
    const [tab, setTab] = useState("order");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/orders-staff/${id}`);
            setOrder(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const isStaff = user && (
        user.role === "admin" || user.role === "editor" ||
        (user.role === "employee" && (
            (user.capabilities || []).includes("mr") ||
            user.designation_name === "Accounts & Billing"
        ))
    );
    const canManage = isStaff;
    const canDelete = user?.role === "admin" || user?.role === "editor";
    const backLink =
        user?.role === "distributor" ? "/distributor/orders" :
        (user?.role === "admin" || user?.role === "editor") ? "/admin/order-status" :
        user?.role === "employee" ? "/employee/order-status" :
        "/orders";

    const changeStatus = async (next) => {
        if (!order || next === order.status) return;
        setBusy(true);
        try {
            await api.put(`/admin/orders/${order.id}/status`, null, { params: { status: next } });
            toast.success(`Status → ${next}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Status update failed");
        } finally {
            setBusy(false);
        }
    };

    const sendRetryLink = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/orders/${order.id}/payment-retry-link`);
            setRetry(data);
            const channels = [data.whatsapp?.sent && "WhatsApp", data.email?.sent && "email"].filter(Boolean);
            toast.success(channels.length ? `Pay link sent via ${channels.join(" + ")}` : "Pay link generated — send it manually below");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not generate the payment link");
        } finally {
            setBusy(false);
        }
    };

    const deleteOrder = async () => {        if (!order) return;
        setBusy(true);
        try {
            await api.delete(`/admin/orders/${order.id}`);
            toast.success(`Order #${order.id.slice(-8).toUpperCase()} deleted`);
            navigate(backLink);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
            setBusy(false);
        }
    };

    if (loading) return <div className="max-w-4xl mx-auto p-8 text-stone-500">Loading…</div>;
    if (!order) return <div className="max-w-4xl mx-auto p-8 text-stone-500">Order not found.</div>;

    const currentStepIdx = STATUS_STEPS.findIndex((s) => s.key === order.status);
    const tc = order.transport_copy;

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6 print:p-0" data-testid="order-detail-page">
            <div className="flex items-start justify-between flex-wrap gap-3 print:hidden">
                <div>
                    <Link to={backLink} className="text-xs uppercase tracking-widest text-sky-700 inline-flex items-center mb-2"><ArrowLeft className="w-3 h-3 mr-1" />Back to orders</Link>
                    <h1 className="font-serif text-3xl text-stone-900">Order #{order.id.slice(-8).toUpperCase()}</h1>
                    <p className="text-xs text-stone-500 mt-1 font-mono">{order.created_at}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {canDelete && (
                        <Select value={order.status} onValueChange={changeStatus} disabled={busy}>
                            <SelectTrigger className="w-[170px] h-9 rounded-none border-amber-400 text-sm bg-amber-50 hover:bg-amber-100" data-testid="detail-status-edit">
                                <SelectValue placeholder="Status…" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="placed">placed</SelectItem>
                                <SelectItem value="confirmed">confirmed</SelectItem>
                                <SelectItem value="packing">packing</SelectItem>
                                <SelectItem value="packed">packed</SelectItem>
                                <SelectItem value="shipped">shipped</SelectItem>
                                <SelectItem value="delivered">delivered</SelectItem>
                                <SelectItem value="cancelled">cancelled</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                    {canManage && order.invoice_number && (
                        <Button onClick={() => setUploadOpen(true)} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="open-tc-upload">
                            <Upload className="w-4 h-4 mr-2" />{tc ? "Replace transport copy" : "Upload transport copy"}
                        </Button>
                    )}
                    {canManage && order.payment_status !== "paid" && (
                        <Button onClick={sendRetryLink} disabled={busy} variant="outline" className="rounded-none border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid="send-payment-retry-link">
                            <MessageCircle className="w-4 h-4 mr-2" />Send Payment Retry Link
                        </Button>
                    )}
                    <Button onClick={() => window.print()} variant="outline" className="rounded-none border-stone-400" data-testid="print-order">
                        <Printer className="w-4 h-4 mr-2" />Print
                    </Button>
                    {canDelete && (
                        <Button onClick={() => setConfirmDelete(true)} variant="outline" className="rounded-none border-red-400 text-red-700 hover:bg-red-50" data-testid="detail-order-delete" disabled={busy}>
                            <Trash2 className="w-4 h-4 mr-2" />Delete order
                        </Button>
                    )}
                </div>
            </div>

            {/* Print-only header */}
            <div className="hidden print:block">
                <div className="text-center mb-6">
                    <div className="text-xs uppercase tracking-widest text-sky-700">Hahnemann Pharmacy & Laboratory</div>
                    <h1 className="font-serif text-2xl mt-1">Order #{order.id.slice(-8).toUpperCase()}</h1>
                    <div className="text-xs text-stone-600 font-mono">{order.created_at}</div>
                </div>
            </div>

            {/* Tabs — staff get the customer profile alongside the order */}
            {canManage && (
                <div className="flex gap-1 border-b border-stone-200 print:hidden" data-testid="order-detail-tabs">
                    {[["order", "Order & dispatch"], ["customer", "Customer details"]].map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            className={`px-4 py-2 text-sm transition-colors border-b-2 -mb-px ${tab === key ? "border-sky-700 text-sky-800 font-medium" : "border-transparent text-stone-500 hover:text-stone-800"}`}
                            data-testid={`order-tab-${key}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}

            {tab === "customer" && canManage ? (
                <OrderCustomerPanel orderId={order.id} detailBase={backLink} />
            ) : (
            <>
            {/* Status timeline */}
            {order.status !== "cancelled" ? (
                <div className="bg-white border border-stone-200 p-6">
                    <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Status timeline</div>
                    <ol className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="status-timeline">
                        {STATUS_STEPS.map((step, idx) => {
                            const done = idx <= currentStepIdx;
                            const active = idx === currentStepIdx;
                            return (
                                <li key={step.key} className={`flex flex-col items-center text-center p-2 border ${active ? "border-sky-700 bg-sky-50" : done ? "border-emerald-300 bg-emerald-50" : "border-stone-200"}`} data-testid={`step-${step.key}`}>
                                    <div className={`${done ? "text-emerald-700" : "text-stone-400"} mb-1`}>{step.icon}</div>
                                    <div className={`text-[10px] uppercase tracking-widest ${active ? "text-sky-700 font-semibold" : done ? "text-emerald-700" : "text-stone-500"}`}>{step.label}</div>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            ) : (
                <div className="bg-red-50 border border-red-200 p-4 text-red-800 text-sm">Order cancelled.</div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
                <Section title="Customer">
                    <Row label="Name" value={order.address?.name || order.user_name || "—"} />
                    <Row label="Email" value={order.customer_email || "—"} testId="detail-customer-email" />
                    <Row label="Phone" value={order.address?.phone || "—"} />
                    <Row label="Address" value={[order.address?.line1, order.address?.line2].filter(Boolean).join(", ") || "—"} />
                    <Row label="City / State" value={`${order.address?.city || "—"}${order.address?.state ? `, ${order.address.state}` : ""}`} />
                    <Row label="Pincode" value={order.address?.pincode || "—"} />
                </Section>
                <Section title="Order">
                    <Row label="Type" value={order.order_type} />
                    <Row label="Placed by" value={`${order.user_name} (${order.user_role})`} />
                    {order.on_behalf_of && <Row label="On behalf of" value={order.on_behalf_of.name} />}
                    <Row label="Invoice" value={order.invoice_number || "—"} />
                    <Row label="Payment" value={`${order.payment_method?.toUpperCase()} · ${order.payment_status}`} />
                    <Row label="Billing" value={order.billing_status} />
                </Section>
            </div>

            {/* Items */}
            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 border-b border-stone-200">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Item</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Size</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Qty</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Rate</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(order.items || []).map((it, i) => (
                            <tr key={i} className="border-b border-stone-100" data-testid={`item-${i}`}>
                                <td className="p-3 text-stone-900">{it.product_name}</td>
                                <td className="p-3 text-stone-700">{it.size_label}</td>
                                <td className="p-3 text-right font-mono">{it.quantity}</td>
                                <td className="p-3 text-right font-mono">{formatINR(it.price)}</td>
                                <td className="p-3 text-right font-mono">{formatINR((it.price || 0) * (it.quantity || 0))}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr><td colSpan={4} className="p-2 text-right text-xs uppercase tracking-widest text-stone-500">Subtotal</td><td className="p-2 text-right font-mono">{formatINR(order.subtotal)}</td></tr>
                        <tr><td colSpan={4} className="p-2 text-right text-xs uppercase tracking-widest text-stone-500">Shipping</td><td className="p-2 text-right font-mono">{formatINR(order.shipping)}</td></tr>
                        {order.discount > 0 && (
                            <tr data-testid="detail-coupon-discount"><td colSpan={4} className="p-2 text-right text-xs uppercase tracking-widest text-emerald-700">Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}</td><td className="p-2 text-right font-mono text-emerald-700">− {formatINR(order.discount)}</td></tr>
                        )}
                        {order.prepaid_discount > 0 && (
                            <tr data-testid="detail-prepaid-discount"><td colSpan={4} className="p-2 text-right text-xs uppercase tracking-widest text-emerald-700">Prepaid discount ({order.prepaid_discount_pct || 5}%)</td><td className="p-2 text-right font-mono text-emerald-700">− {formatINR(order.prepaid_discount)}</td></tr>
                        )}
                        <tr><td colSpan={4} className="p-2 text-right text-xs uppercase tracking-widest text-stone-500 font-semibold">Total</td><td className="p-2 text-right font-mono text-sky-700 text-base font-semibold">{formatINR(order.total)}</td></tr>
                    </tfoot>
                </table>
            </div>

            {/* Payment retry link (staff only, unpaid orders) */}
            {canManage && retry && (
                <div className="bg-emerald-50 border border-emerald-300 p-5 print:hidden" data-testid="retry-link-panel">
                    <div className="text-xs uppercase tracking-widest text-emerald-800 mb-2">Pay-now link · {formatINR(retry.amount)}</div>
                    <div className="font-mono text-xs break-all bg-white border border-emerald-200 px-3 py-2" data-testid="retry-link-url">{retry.link}</div>
                    <div className="flex gap-2 flex-wrap mt-3">
                        <Button
                            variant="outline"
                            className="rounded-none border-emerald-500 text-emerald-700"
                            onClick={() => { navigator.clipboard.writeText(retry.link); toast.success("Link copied"); }}
                            data-testid="retry-link-copy"
                        >
                            <Copy className="w-4 h-4 mr-2" />Copy link
                        </Button>
                        <a
                            href={retry.wa_manual_link}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center px-4 py-2 text-sm"
                            data-testid="retry-link-whatsapp"
                        >
                            <ExternalLink className="w-4 h-4 mr-2" />Open in WhatsApp
                        </a>
                    </div>
                    <div className="text-[11px] text-emerald-900 mt-3">
                        Auto-send: WhatsApp {retry.whatsapp?.sent ? "✓ sent" : `— ${retry.whatsapp?.reason || "not sent"}`} · Email {retry.email?.sent ? "✓ sent" : `— ${retry.email?.reason || "not sent"}`}.
                        Use <em>Open in WhatsApp</em> to send it yourself from the company number.
                    </div>
                </div>
            )}

            {/* Transport copy section */}
            <Section title="Transport / Dispatch" testId="transport-section">
                {tc ? (
                    <div className="space-y-2">
                        <Row label="LR number" value={tc.lr_number || "—"} />
                        <Row label="Transporter" value={tc.transporter || "—"} />
                        <Row label="Uploaded by" value={`${tc.uploaded_by_email} (${tc.uploaded_by_role})`} />
                        <Row label="Uploaded on" value={(tc.uploaded_at || "").slice(0, 19).replace("T", " ")} />
                        {tc.notes && <Row label="Notes" value={tc.notes} />}
                        <div className="mt-3 print:hidden">
                            <a href={tc.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-500 text-emerald-700 hover:bg-emerald-50 text-sm" data-testid="download-transport">
                                <FileText className="w-4 h-4" />Open / download {tc.file_name}
                            </a>
                        </div>
                    </div>
                ) : (
                    <div className="text-sm text-stone-500">
                        No transport copy uploaded yet.
                        {canManage && order.invoice_number && (
                            <div className="text-xs text-stone-600 mt-2">Click "Upload transport copy" above and provide bill number <strong className="font-mono">{order.invoice_number}</strong>.</div>
                        )}
                        {canManage && !order.invoice_number && (
                            <div className="text-xs text-amber-700 mt-2">This order does not have an invoice yet. Generate the invoice first, then come back to upload the transport copy.</div>
                        )}
                    </div>
                )}
            </Section>

            {/* Packing detail (visible to staff) */}
            {canManage && order.packing && (
                <Section title="Packing detail">
                    <Row label="Status" value={order.packing.status || "—"} />
                    {order.packing.packed_by && <Row label="Packed by" value={order.packing.packed_by} />}
                    {order.packing.packed_at && <Row label="Packed at" value={order.packing.packed_at} />}
                    {order.packing.notes && <Row label="Notes" value={order.packing.notes} />}
                </Section>
            )}
            </>
            )}

            <TransportCopyUploadDialog
                open={uploadOpen}
                onOpenChange={setUploadOpen}
                defaultInvoiceNumber={order.invoice_number || ""}
                onUploaded={() => load()}
            />

            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete order #{order.id.slice(-8).toUpperCase()}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This permanently removes the order ({order.order_type} · <strong>{order.address?.name || order.user_name}</strong> · <span className="font-mono">{formatINR(order.total)}</span>).
                            {order.invoice_number && (
                                <span className="block mt-2 text-amber-700">
                                    The linked invoice <strong className="font-mono">{order.invoice_number}</strong> is kept (just unlinked) so the books stay clean. Delete it separately from Accounts → Invoices if you need to.
                                </span>
                            )}
                            <span className="block mt-2 text-xs">This action is logged for admins.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="detail-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteOrder} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="detail-delete-confirm">
                            Delete order
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

const Section = ({ title, children, testId }) => (
    <div className="bg-white border border-stone-200 p-5" data-testid={testId}>
        <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">{title}</div>
        <div className="space-y-1">{children}</div>
    </div>
);

const Row = ({ label, value, testId }) => (
    <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="text-stone-500 text-xs uppercase tracking-widest">{label}</div>
        <div className="col-span-2 text-stone-900" data-testid={testId}>{value}</div>
    </div>
);
